import {Component, computed, effect, inject, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {AuthService} from "../../../../../shared/src/lib/auth/auth/data-access/auth.service";
import {MatButtonModule} from "@angular/material/button";
import {MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule} from "@angular/material/form-field";
import {MatSelectModule} from "@angular/material/select";
import {FormControl, FormsModule, ReactiveFormsModule} from "@angular/forms";
import {MatInputModule} from "@angular/material/input";
import {MatIconModule} from "@angular/material/icon";
import {ContractTileComponent} from "./ui/contract-tile/contract-tile.component";
import {MatPaginatorModule} from "@angular/material/paginator";
import {SubscriptionOverviewService} from "../home/data-access/subscription-overview.service";
import {MatDialog} from '@angular/material/dialog';
import {ContractNameDialogComponent} from './ui/contract-name-dialog/contract-name-dialog.component';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'dashboard',
    standalone: true,
    imports: [
        MatButtonModule,
        MatFormFieldModule,
        MatSelectModule,
        MatInputModule,
        FormsModule,
        ReactiveFormsModule,
        MatIconModule,
        ContractTileComponent,
        MatPaginatorModule,
        TranslateModule
    ],
    providers: [
        {
            provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
            useValue: {
                subscriptSizing: 'dynamic',
                appearance: 'outline'
            }
        }
    ],
    templateUrl: './dashboard.component.html',
    styleUrl: './dashboard.component.scss'
})
export class DashboardComponent implements OnInit {

    public searchFormControl = new FormControl('');
    public statusFormControl = new FormControl(0);
    public sortFormControl = new FormControl(1);
    public workflowName!: string;
    public subscriptionOverview = inject(SubscriptionOverviewService);
    public workflow = '';
    public contractStatuses = computed<{ yes: number, no: number, check: number, all: number }>(() => {
        const workflowData = this.subscriptionOverview.subscriptionData()?.contentCollection;
        if (!workflowData) {
            return {
                all: 0,
                yes: 0,
                no: 0,
                check: 0,
            };
        }

        return workflowData.reduce((acc, item) => {
            if (item.contentCollectionType === 'contract') {
                acc = { ...acc, all: acc.all + 1 };
                switch (item.activeStep.toString().substring(0, 1)) {
                    case '2':
                        return {
                            ...acc,
                            yes: acc.yes + 1,
                        };
                    case '3':
                        return {
                            ...acc,
                            no: acc.no + 1,
                        };
                    case '4':
                        return {
                            ...acc,
                            check: acc.check + 1,
                        };
                    default:
                        return acc;
                }
            } else {
                return acc;
            }
        }, {
            all: 0,
            yes: 0,
            no: 0,
            check: 0,
        });
    });


    public constructor(public authService: AuthService,
                       public dialog: MatDialog,
                       private subscriptionService: SubscriptionOverviewService,
                       private router: Router) {
        this.onNewContract();
    }

    public ngOnInit(): void {
        this.subscriptionService.getSubscriptionData$.next();
        this.sortFormControl.valueChanges.subscribe(value => {
            this.sortWorkflows(value === 1 ? 'descend' : 'ascend');
        });
        this.statusFormControl.valueChanges.subscribe(value => {
            if (value !== null) {
                this.setWorkflowStatusFilter(value)
            }
        });
        this.searchFormControl.valueChanges.subscribe((value) => {
            if (value !== null) {
                this.searchWorkflows(value)
            }
        });
    }

    public newContract(): void {
        let dialogRef = this.dialog.open(ContractNameDialogComponent, {
            height: "fit-content",
            width: "max(200px, 400px)",
            data: {name: this.workflowName},
        });

        dialogRef.afterClosed()
            .subscribe((workflowName: string) => {
                if (workflowName) {
                    this.createNewWorkflow(workflowName, "contract");
                }
            });
    }

    public createNewWorkflow(workflowName: string, workflowType: string): void {
        this.subscriptionOverview.createNewWorkflow$.next([
            workflowName,
            workflowType,
        ]);
    }

    public sortWorkflows(order: 'ascend' | 'descend') {
        this.subscriptionService.sortState.update(() => ({
            ascend: order === 'ascend',
            descend: order === 'descend'
        }));
    }

    public setWorkflowStatusFilter(status: number) {
        this.subscriptionService.filterStatusState.set(status);
    }

    public searchWorkflows(term: string) {
        this.subscriptionService.searchTerm.set(term.toLowerCase());
    }

    private onNewContract(): void {
        effect(() => {
            const lastWorkflow = this.subscriptionService.lastCreatedWorkflow();
            if (lastWorkflow) {
                this.subscriptionService.lastCreatedWorkflow.set(undefined)
                this.router.navigate(['/contract/', lastWorkflow.uid], {queryParams: {subscription_id: this.subscriptionService.subscriptionId()}});
            }
        }, {allowSignalWrites: true});
    }
}


